'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';

import { ColorDot, Drawer, LogoThemeAware, ThemeToggle } from '@web/ui';

import styles from './MobileNavDrawer.module.scss';
import sideStyles from './SideNav.module.scss';
import { NavGroup, NavItem } from './types';
import { UserNavItem } from './UserNavItem';

import type { MouseEvent, ReactNode } from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  footerTopContent?: (ctx: { isExpanded: boolean }) => ReactNode;
};

function isGroup(item: NavItem): item is NavGroup {
  return (item as NavGroup).children !== undefined;
}

function normalizePath(path: string) {
  const normalized = path.replace(/\/+$/, '');
  return normalized || '/';
}

function isRouteActive(currentPath: string, href: string) {
  const current = normalizePath(currentPath);
  const target = normalizePath(href);
  return current === target || current.startsWith(`${target}/`);
}

function NavIcon({
  item,
}: {
  item: Pick<
    NavItem,
    'iconIdle' | 'iconHover' | 'showIndicator' | 'indicatorLabel'
  >;
}) {
  return (
    <span className={sideStyles.iconWrapDual} aria-hidden>
      <span className={sideStyles.iconIdle}>{item.iconIdle}</span>
      <span className={sideStyles.iconHover}>{item.iconHover}</span>
      {item.showIndicator ? (
        <span
          className={sideStyles.itemIndicator}
          aria-label={item.indicatorLabel}
        >
          <ColorDot size="lg" color="var(--nn-color-primary)" />
        </span>
      ) : null}
    </span>
  );
}

export function MobileNavDrawer({
  open,
  onClose,
  items,
  footerTopContent,
}: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (key: string) =>
    setOpenGroups((s) => ({ ...s, [key]: !s[key] }));

  const handleGroupClick = (group: NavGroup) => {
    if (!group.defaultHref) {
      toggleGroup(group.key);
      return;
    }

    if (!isRouteActive(pathname, group.defaultHref)) {
      router.push(group.defaultHref);
    }

    onClose();
  };

  const handleNavLinkClick =
    (href: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (!isRouteActive(pathname, href)) {
        router.push(href);
      }
      onClose();
    };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="left"
      width={320}
      paperClassName={styles.drawerPaper}
    >
      <div className={styles.header}>
        <div className={styles.logo}>
          <LogoThemeAware />
        </div>

        <ThemeToggle />
      </div>

      <div className={styles.content}>
        <div className={styles.navItems}>
          {items.map((it) => {
            if (!isGroup(it)) {
              const active = isRouteActive(pathname, it.href);
              const itemClassName = `${sideStyles.item} ${
                active ? sideStyles.itemActive : ''
              } ${it.disabled ? sideStyles.itemDisabled : ''}`;

              if (it.disabled) {
                return (
                  <button
                    key={it.key}
                    type="button"
                    className={itemClassName}
                    onClick={() => {
                      if (it.disabledHref) {
                        router.push(it.disabledHref);
                      }
                      onClose();
                    }}
                    aria-disabled="true"
                  >
                    <NavIcon item={it} />
                    <span
                      className={sideStyles.label}
                      style={{ display: 'block', opacity: 1 }}
                    >
                      {it.label}
                    </span>
                  </button>
                );
              }

              return (
                <Link
                  key={it.key}
                  href={it.href}
                  className={itemClassName}
                  aria-current={active ? 'page' : undefined}
                  prefetch
                  onClick={handleNavLinkClick(it.href)}
                >
                  <NavIcon item={it} />
                  <span
                    className={sideStyles.label}
                    style={{ display: 'block', opacity: 1 }}
                  >
                    {it.label}
                  </span>
                </Link>
              );
            }

            const hasActiveChild = it.children.some((sub) =>
              isRouteActive(pathname, sub.href),
            );
            const isOpen = openGroups[it.key] ?? hasActiveChild;
            return (
              <div key={it.key}>
                <button
                  type="button"
                  className={`${sideStyles.item} ${hasActiveChild ? sideStyles.itemActive : ''}`}
                  onClick={() => handleGroupClick(it)}
                  aria-expanded={isOpen}
                >
                  <NavIcon item={it} />
                  <span
                    className={sideStyles.label}
                    style={{ display: 'block', opacity: 1 }}
                  >
                    {it.label}
                  </span>
                </button>

                <div
                  className={sideStyles.submenu}
                  data-open={isOpen}
                  style={{
                    // ! Mobile drawer clipping guard: do not hard-cap open submenu height on mobile.
                    maxHeight: isOpen ? 'none' : 0,
                    opacity: isOpen ? 1 : 0,
                    overflow: isOpen ? 'visible' : 'hidden',
                  }}
                >
                  {it.children.map((sub: any) => {
                    const active = isRouteActive(pathname, sub.href);
                    return (
                      <Link
                        key={sub.key}
                        href={sub.href}
                        className={`${sideStyles.submenuItem} ${active ? sideStyles.submenuItemActive : ''}`}
                        aria-current={active ? 'page' : undefined}
                        prefetch
                        onClick={handleNavLinkClick(sub.href)}
                      >
                        <span
                          className={sideStyles.label}
                          style={{ display: 'block', opacity: 1 }}
                        >
                          {sub.label}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.userSection}>
          {footerTopContent && (
            <div className={styles.userSectionTop}>
              {footerTopContent({ isExpanded: true })}
            </div>
          )}

          <UserNavItem />
        </div>
      </div>
    </Drawer>
  );
}
